import ShortcutHint, {
  ShortcutKey,
} from 'components/common/ShortcutHint/ShortcutHint';
import {
  ROTATION_MENU_COPY_LABEL,
  ROTATION_MENU_DELETE_LABEL,
  ROTATION_MENU_EDIT_LABEL,
  ROTATION_MENU_PASTE_LABEL,
  ROTATION_SHORTCUT_COPY,
  ROTATION_SHORTCUT_DELETE,
  ROTATION_SHORTCUT_EDIT,
  ROTATION_SHORTCUT_PASTE,
} from 'constants/uiConstants';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LuClipboard, LuCopy, LuPencil, LuTrash2 } from 'react-icons/lu';
import { pxToRem } from 'utils/cssUnits';

/** Breathing room kept between the menu and the edge of the viewport. */
const VIEWPORT_MARGIN_PX = 8;

interface RotationContextMenuProps {
  x: number;
  y: number;
  canEdit: boolean;
  canPaste: boolean;
  /** Plans in the selection that can be deleted; 0 hides Delete. */
  deleteCount: number;
  onEdit: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onDelete: () => void;
  onClose: () => void;
}

const MenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  /** A single key, or a Ctrl/⌘ combination with `withCommand`. */
  shortcut: string;
  withCommand?: boolean;
  danger?: boolean;
  onClick: () => void;
}> = ({ icon, label, shortcut, withCommand, danger, onClick }) => (
  <button
    type="button"
    role="menuitem"
    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-rowHover ${
      danger ? 'text-danger-dark' : 'text-charcoal'
    }`}
    onClick={onClick}
  >
    {icon}
    <span className="flex-1">{label}</span>
    {withCommand ? (
      <ShortcutHint shortcutKey={shortcut} variant="subtle" />
    ) : (
      <ShortcutKey variant="subtle">{shortcut}</ShortcutKey>
    )}
  </button>
);

/**
 * The Rotation planner's right-click menu (OP-2501): set crop, copy, paste
 * and delete, each with its keyboard shortcut. Positioned at the pointer and
 * kept inside the viewport, like the Tasks grid's row menu
 * (`.row-actions-menu`). Escape, a click elsewhere or scrolling closes it.
 */
const RotationContextMenu: React.FC<RotationContextMenuProps> = ({
  x,
  y,
  canEdit,
  canPaste,
  deleteCount,
  onEdit,
  onCopy,
  onPaste,
  onDelete,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const { width, height } = element.getBoundingClientRect();
    setPosition({
      left: Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(x, window.innerWidth - width - VIEWPORT_MARGIN_PX)
      ),
      top: Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(y, window.innerHeight - height - VIEWPORT_MARGIN_PX)
      ),
    });
    element.querySelector<HTMLButtonElement>('button')?.focus();
  }, [x, y]);

  useEffect(() => {
    const handlePointer = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const run = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <div
      ref={ref}
      role="menu"
      className="row-actions-menu fixed z-[60] w-max min-w-[13rem] rounded-md border border-gray-bright bg-white py-1 shadow-lg"
      // Runtime coordinates, consumed by .row-actions-menu in App.css
      style={
        {
          '--row-menu-left': `${pxToRem(position.left)}rem`,
          '--row-menu-top': `${pxToRem(position.top)}rem`,
        } as React.CSSProperties
      }
    >
      {canEdit && (
        <MenuItem
          icon={<LuPencil size={16} aria-hidden />}
          label={ROTATION_MENU_EDIT_LABEL}
          shortcut={ROTATION_SHORTCUT_EDIT}
          onClick={run(onEdit)}
        />
      )}
      <MenuItem
        icon={<LuCopy size={16} aria-hidden />}
        label={ROTATION_MENU_COPY_LABEL}
        shortcut={ROTATION_SHORTCUT_COPY}
        withCommand
        onClick={run(onCopy)}
      />
      {canPaste && (
        <MenuItem
          icon={<LuClipboard size={16} aria-hidden />}
          label={ROTATION_MENU_PASTE_LABEL}
          shortcut={ROTATION_SHORTCUT_PASTE}
          withCommand
          onClick={run(onPaste)}
        />
      )}
      {deleteCount > 0 && (
        <MenuItem
          icon={<LuTrash2 size={16} aria-hidden />}
          label={ROTATION_MENU_DELETE_LABEL(deleteCount)}
          shortcut={ROTATION_SHORTCUT_DELETE}
          danger
          onClick={run(onDelete)}
        />
      )}
    </div>
  );
};

export default RotationContextMenu;
